import apiService from './api';
import { Despesa, ApiResponse, RespostaPaginada } from '../types';

export interface DespesasFilters {
  veiculo_id?: number;
  motorista_id?: number;
  categoria?: string;
  tipo?: string;
  status?: string;
  data_inicio?: string;
  data_fim?: string;
  valor_min?: number;
  valor_max?: number;
  fornecedor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

export interface DespesasEstatisticas {
  total_despesas: number;
  valor_total: number;
  valor_medio: number;
  despesas_pagas: number;
  despesas_pendentes: number;
  despesas_vencidas: number;
  por_categoria: Record<string, {
    quantidade: number;
    valor_total: number;
    valor_medio: number;
  }>;
  por_tipo: Record<string, {
    quantidade: number;
    valor_total: number;
    valor_medio: number;
  }>;
  por_status: Record<string, {
    quantidade: number;
    valor_total: number;
    valor_medio: number;
  }>;
}

export const despesasService = {
  /**
   * Listar despesas com filtros
   */
  getDespesas: async (filters: DespesasFilters = {}): Promise<RespostaPaginada<Despesa>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<RespostaPaginada<Despesa>>(`/v1/despesas?${params.toString()}`);
    return response;
  },

  /**
   * Buscar despesa por ID
   */
  getDespesa: async (id: number): Promise<ApiResponse<Despesa>> => {
    const response = await apiService.get<ApiResponse<Despesa>>(`/v1/despesas/${id}`);
    return response;
  },

  /**
   * Criar nova despesa
   */
  createDespesa: async (data: Partial<Despesa>): Promise<ApiResponse<Despesa>> => {
    const response = await apiService.post<ApiResponse<Despesa>>('/v1/despesas', data);
    return response;
  },

  /**
   * Atualizar despesa
   */
  updateDespesa: async (id: number, data: Partial<Despesa>): Promise<ApiResponse<Despesa>> => {
    const response = await apiService.put<ApiResponse<Despesa>>(`/v1/despesas/${id}`, data);
    return response;
  },

  /**
   * Excluir despesa
   */
  deleteDespesa: async (id: number): Promise<ApiResponse<null>> => {
    const response = await apiService.delete<ApiResponse<null>>(`/v1/despesas/${id}`);
    return response;
  },

  /**
   * Listar despesas de um veículo específico
   */
  getByVeiculo: async (veiculoId: number, filters: DespesasFilters = {}): Promise<RespostaPaginada<Despesa>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<RespostaPaginada<Despesa>>(`/v1/despesas/veiculo/${veiculoId}?${params.toString()}`);
    return response;
  },

  /**
   * Obter estatísticas de despesas
   */
  getEstatisticas: async (filters: DespesasFilters = {}): Promise<ApiResponse<DespesasEstatisticas>> => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiService.get<ApiResponse<DespesasEstatisticas>>(`/v1/despesas/estatisticas?${params.toString()}`);
    return response;
  },

  /**
   * Marcar despesa como paga
   */
  marcarComoPaga: async (id: number, formaPagamento: string, dataPagamento?: string): Promise<ApiResponse<Despesa>> => {
    const response = await apiService.post<ApiResponse<Despesa>>(`/v1/despesas/${id}/pagar`, {
      forma_pagamento: formaPagamento,
      data_pagamento: dataPagamento
    });
    return response;
  }
};
